import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Youtube, Send, ArrowRight, Bell, BookMarked, GraduationCap, Sparkles, Play, Quote } from "lucide-react";
import api, { API } from "@/lib/api";
import { SectionTitle } from "@/components/Section";
import SubjectIcon from "@/components/SubjectIcon";
import KitCard from "@/components/KitCard";
import AuthGateModal from "@/components/AuthGateModal";
import { useKitAccess } from "@/lib/useKitAccess";
import { CONTACT, COMING_SOON } from "@/lib/constants";

export default function Home() {
  const [kits, setKits] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [courses, setCourses] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [gateOpen, setGateOpen] = useState(false);
  const [pendingNext, setPendingNext] = useState(null);
  const openKit = useKitAccess(setGateOpen, setPendingNext);

  useEffect(() => {
    api.get("/kits?limit=6").then(({ data }) => setKits(data)).catch(() => {});
    api.get("/subjects").then(({ data }) => setSubjects(data)).catch(() => {});
    api.get("/courses").then(({ data }) => setCourses(data)).catch(() => {});
    api.get("/announcements?limit=4").then(({ data }) => setAnnouncements(data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0B2545]">
        <img
          src="https://images.unsplash.com/photo-1514369118554-e20d93546b30?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 md:py-24">
          <div className="max-w-2xl fade-up">
            <span className="inline-flex items-center gap-1.5 rounded-sm bg-[#FF9933] px-3 py-1 text-xs font-bold uppercase tracking-wide text-slate-900">
              <Sparkles size={13} /> 100% Free · No hidden charges
            </span>
            <h1 className="mt-4 font-serif text-3xl font-bold leading-tight text-white md:text-5xl">
              Free Subject-wise Revision Kits for UPSC CSE
            </h1>
            <p className="mt-4 text-base leading-relaxed text-slate-200 md:text-lg">
              Crisp, exam-ready revision material curated by educator Arpita Sharma — organised subject-wise so you revise smart, not hard.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/revision-kits" className="inline-flex items-center gap-2 rounded-sm bg-[#FF9933] px-5 py-3 text-sm font-bold text-slate-900 transition-colors hover:bg-[#E68A2E]" data-testid="hero-get-kits">
                Get Free Revision Kits <ArrowRight size={16} />
              </Link>
              <Link to="/courses" className="inline-flex items-center gap-2 rounded-sm border border-white/60 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10" data-testid="hero-courses">
                Explore Courses
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4">
        {/* Important Notices */}
        <section className="py-10">
          <div className="gov-card">
            <div className="gov-card-header flex items-center gap-2 rounded-t-sm px-4 py-2.5 font-serif text-base font-bold">
              <Bell size={17} /> Important Notices
            </div>
            <ul className="divide-y divide-[#E5E7EB]" data-testid="important-notices">
              {announcements.length === 0 && <li className="px-4 py-3 text-sm text-gray-500">No notices yet.</li>}
              {announcements.map((a) => (
                <li key={a.id} className="flex items-start gap-3 px-4 py-3">
                  <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${a.is_important ? "bg-[#D97706]" : "bg-[#1F3A68]"}`} />
                  <div>
                    <p className="text-sm font-semibold text-[#0B2545]">
                      {a.title}
                      {a.is_important && <span className="ml-2 rounded-sm bg-[#D97706]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#D97706]">New</span>}
                    </p>
                    <p className="text-sm text-gray-600">{a.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Latest Kits */}
        <section className="pb-10">
          <div className="flex items-end justify-between">
            <SectionTitle sub="The six newest additions to the library" testId="latest-kits-title">Latest Revision Kits</SectionTitle>
            <Link to="/revision-kits" className="mb-6 hidden shrink-0 items-center gap-1 text-sm font-semibold text-[#0056B3] hover:underline sm:flex" data-testid="view-all-kits">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {kits.map((k) => <KitCard key={k.id} kit={k} onOpen={openKit} />)}
          </div>
        </section>

        {/* Subjects grid */}
        <section className="pb-10">
          <SectionTitle sub="Browse the entire library organised by subject" testId="subjects-title">Subjects</SectionTitle>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {subjects.map((s) => (
              <Link
                key={s.id}
                to={`/revision-kits?subject=${s.slug}`}
                className="gov-card group flex items-center gap-3 p-4 transition-all hover:-translate-y-0.5 hover:shadow-md"
                data-testid={`subject-${s.slug}`}
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm bg-[#1F3A68]/10 group-hover:bg-[#1F3A68]/20">
                  <SubjectIcon name={s.icon} />
                </div>
                <div className="min-w-0">
                  <div className="truncate font-serif text-sm font-bold text-[#0B2545]">{s.name}</div>
                  <div className="text-xs text-gray-500">{s.kit_count} kit{s.kit_count === 1 ? "" : "s"}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Meet your mentor */}
        <section className="pb-10">
          <div className="gov-card overflow-hidden" data-testid="meet-mentor">
            <div className="flex flex-col md:flex-row">
              <img
                src={`${API}/site/about-photo`}
                alt="Arpita Sharma"
                className="h-56 w-full object-cover md:h-auto md:w-72 md:shrink-0"
              />
              <div className="flex flex-col justify-center p-5 md:p-7">
                <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[#D97706]">
                  <Quote size={15} /> Meet your mentor
                </span>
                <h2 className="mt-2 font-serif text-2xl font-bold text-[#0B2545]">Arpita Sharma</h2>
                <p className="text-sm text-gray-500">Founder &amp; Educator, UPSC With Arpita</p>
                <p className="mt-3 font-serif text-base italic leading-relaxed text-[#0B2545]">
                  “The right notes, shared freely, can change a serious aspirant's journey. Revise smart, stay consistent, and trust the process — I'm with you at every step.”
                </p>
                <Link to="/about" className="mt-4 inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-[#0056B3] hover:underline" data-testid="meet-mentor-more">
                  Read more about Arpita <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Courses */}
        <section className="pb-10">
          <SectionTitle sub="Go deeper with Arpita's structured, mentor-led programmes" testId="home-courses-title">Courses by Arpita</SectionTitle>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {courses.map((c) => (
              <div key={c.id} className="gov-card flex flex-col overflow-hidden md:flex-row" data-testid={`home-course-${c.slug}`}>
                <img src={c.image} alt={c.title} className="h-44 w-full object-cover md:h-auto md:w-44" />
                <div className="flex flex-1 flex-col p-4">
                  <span className="inline-block w-fit rounded-sm bg-[#D97706]/10 px-2 py-0.5 text-[11px] font-bold uppercase text-[#D97706]">Paid Course</span>
                  <h3 className="mt-2 font-serif text-lg font-bold text-[#0B2545]">{c.title}</h3>
                  <p className="mt-1 line-clamp-2 text-sm text-gray-600">{c.subtitle}</p>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="font-serif text-xl font-bold text-[#0B2545]">₹{c.price}</span>
                    {c.original_price && <span className="text-sm text-gray-400 line-through">₹{c.original_price}</span>}
                  </div>
                  <Link to={`/courses/${c.slug}`} className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-sm bg-[#1F3A68] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0B2545]" data-testid={`home-course-view-${c.slug}`}>
                    View Details <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Coming Soon */}
        <section className="pb-10">
          <SectionTitle sub="More free resources are on the way" testId="coming-soon-title">Coming Soon</SectionTitle>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {COMING_SOON.map((c) => {
              const slug = c.title.toLowerCase().replace(/\s+/g, "-");
              return (
                <div key={c.title} className="gov-card flex flex-col p-4" data-testid={`coming-soon-${slug}`}>
                  <span className={`w-fit rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${c.live ? "bg-[#16A34A]/10 text-[#16A34A]" : "bg-gray-100 text-gray-500"}`}>{c.live ? "Live Now" : "Soon"}</span>
                  <h3 className="mt-2 font-serif text-base font-bold text-[#0B2545]">{c.title}</h3>
                  <p className="mt-1 flex-1 text-sm text-gray-600">{c.desc}</p>
                  {c.live ? (
                    <Link to={c.to || "/mock-tests"} className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-sm bg-[#FF9933] px-3 py-1.5 text-xs font-bold text-slate-900 transition-colors hover:bg-[#E68A2E]" data-testid={`attempt-${slug}`}>
                      <Play size={12} /> Attempt now
                    </Link>
                  ) : (
                    <a href={CONTACT.telegram} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-sm border border-[#1F3A68] px-3 py-1.5 text-xs font-semibold text-[#1F3A68] transition-colors hover:bg-[#1F3A68] hover:text-white" data-testid={`notify-${slug}`}>
                      <Send size={12} /> Get notified on Telegram
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Social CTA */}
        <section className="pb-12">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <a href={CONTACT.youtube} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between rounded-sm bg-[#C4302B] p-6 text-white transition-transform hover:-translate-y-0.5" data-testid="cta-youtube">
              <div>
                <div className="flex items-center gap-2 font-serif text-xl font-bold"><Youtube /> Watch on YouTube</div>
                <p className="mt-1 text-sm text-white/90">Free lectures, strategy sessions & live doubt-solving.</p>
              </div>
              <ArrowRight />
            </a>
            <a href={CONTACT.telegram} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between rounded-sm bg-[#229ED9] p-6 text-white transition-transform hover:-translate-y-0.5" data-testid="cta-telegram">
              <div>
                <div className="flex items-center gap-2 font-serif text-xl font-bold"><Send /> Join on Telegram</div>
                <p className="mt-1 text-sm text-white/90">Instant kit updates, announcements & community.</p>
              </div>
              <ArrowRight />
            </a>
          </div>
        </section>
      </div>

      <AuthGateModal open={gateOpen} onOpenChange={setGateOpen} nextPath={pendingNext} />
    </div>
  );
}
